import { useCallback, useMemo, useState } from "react";
import { FinancialAccount } from "@common/types/financialAccount";
import {
    CheckForImporterErrorsOptions,
    useCheckForImporterErrors,
} from "@api/financialDocument.api";
import { ImporterContext } from "./importer.context";
import { ImporterDocumentRow, ImporterSort } from "./types";
import { sortDocumentRows } from "./sortHelpers";

interface Props {
    importerQueue: string;
    reconciliedFinancialAccount?: FinancialAccount;
    readyToImportLabel: string;
    children: React.ReactNode;
}

export const ImporterContextProvider: React.FC<Props> = ({
    importerQueue,
    reconciliedFinancialAccount,
    readyToImportLabel,
    children,
}) => {
    const [documentRows, setDocumentRows] = useState<ImporterDocumentRow[]>([]);
    const [previewedRowId, setPreviewedRowId] = useState<string | undefined>();
    const [refreshEnabled, setRefreshEnabled] = useState<boolean>(true);
    const [sort, setSort] = useState<ImporterSort | undefined>();
    const [entityIdByStatementAccountKey, setEntityIdByStatementAccountKey] =
        useState<ReadonlyMap<string, number>>(() => new Map());
    const checkForImporterErrors = useCheckForImporterErrors();

    const statusSortContext = useMemo(
        () => ({ importerQueue, reconciliedFinancialAccount }),
        [importerQueue, reconciliedFinancialAccount],
    );

    const sortedRows = useMemo(
        () =>
            sortDocumentRows({
                rows: documentRows,
                sort,
                ...statusSortContext,
            }),
        [documentRows, sort, statusSortContext],
    );

    const upsertDocumentRow = useCallback(
        (documentRow: ImporterDocumentRow) => {
            const isRenderable =
                !!documentRow.financialDocument || !!documentRow.file;
            if (!isRenderable) {
                return;
            }

            setDocumentRows((prevDocumentRows) => {
                const existingDocumentRowIdx = prevDocumentRows.findIndex(
                    (d) =>
                        d.id === documentRow.id ||
                        (documentRow.financialDocument &&
                            d.financialDocument?.id ===
                                documentRow.financialDocument.id),
                );
                if (existingDocumentRowIdx !== -1) {
                    return prevDocumentRows.map((d, index) =>
                        index === existingDocumentRowIdx ? documentRow : d,
                    );
                }

                return [...prevDocumentRows, documentRow];
            });
        },
        [],
    );

    const updateDocumentRow = useCallback(
        (id: string, documentRow: Partial<Omit<ImporterDocumentRow, "id">>) => {
            setDocumentRows((prevDocumentRows) =>
                prevDocumentRows.map((d) =>
                    d.id === id ? { ...d, ...documentRow } : d,
                ),
            );
        },
        [],
    );

    const revalidateDocument = useCallback(
        (options: CheckForImporterErrorsOptions) => {
            checkForImporterErrors.mutate(options);
        },
        [checkForImporterErrors],
    );

    const removeDocumentRow = useCallback(
        (documentRow: ImporterDocumentRow) => {
            setDocumentRows((prevDocumentRows) =>
                prevDocumentRows.filter((d) => d.id !== documentRow.id),
            );
        },
        [],
    );

    const setStatementAccountEntity = useCallback(
        ({ key, entityId }: { key: string; entityId: number | null }) => {
            setEntityIdByStatementAccountKey((prevEntityIdByKey) => {
                if (prevEntityIdByKey.get(key) === (entityId ?? undefined)) {
                    return prevEntityIdByKey;
                }

                const nextEntityIdByKey = new Map(prevEntityIdByKey);
                if (entityId == null) {
                    nextEntityIdByKey.delete(key);
                } else {
                    nextEntityIdByKey.set(key, entityId);
                }
                return nextEntityIdByKey;
            });
        },
        [],
    );

    const context = useMemo(
        () => ({
            documentRows,
            sortedRows,
            sort,
            setSort,
            upsertDocumentRow,
            removeDocumentRow,
            updateDocumentRow,
            previewedRowId,
            setPreviewedRowId,
            importerQueue,
            reconciliedFinancialAccount,
            revalidateDocument,
            readyToImportLabel,
            refreshEnabled,
            setRefreshEnabled,
            entityIdByStatementAccountKey,
            setStatementAccountEntity,
        }),
        [
            documentRows,
            sortedRows,
            sort,
            previewedRowId,
            importerQueue,
            reconciliedFinancialAccount,
            updateDocumentRow,
            revalidateDocument,
            upsertDocumentRow,
            removeDocumentRow,
            readyToImportLabel,
            refreshEnabled,
            setRefreshEnabled,
            entityIdByStatementAccountKey,
            setStatementAccountEntity,
        ],
    );

    return (
        <ImporterContext.Provider value={context}>
            {children}
        </ImporterContext.Provider>
    );
};
